<script setup lang="ts">
import ModalBase from '@/components/modal/modal-container.vue'
import ModalTitle from '@/components/modal/modal-title.vue'
import ModalContent from '@/components/modal/modal-content.vue'
import ModalFooterCancelAndSave from '@/components/modal/modal-footer-cancel-and-save.vue'
import GridContainer from '@/components/grid-container.vue'
import GridItem from '@/components/grid-item.vue'
import InputLabel from '@/components/input-label.vue'
import InputSelect from '@/components/input-select.vue'
import InputDate from '@/components/input-date.vue'
import { useWheelingAssets } from '@/composables/assets/assets'
import { ref, computed } from 'vue'
import IconHelp from '@/shared/components/IconHelp.vue'
import ContentLoader from '@/components/content-loader.vue'
import HeroSplashError from '@/components/hero-splash-error.vue'
import { useCreateWheelingInvoice } from '@/features/invoices/mutations/invoices'
import useNotifications from '@/composables/notifications'
import { useForm } from 'vee-validate'
import { Log } from '@/shared/utils/logging/loggers'
import { toTypedSchema } from '@vee-validate/zod'
import { createManualInvoiceSchema } from '@/features/invoices/schemas'
import { todayDateOnly } from '@/shared/date-only'

const emit = defineEmits<{
    close: []
    submit: [{assetCode: string, invoiceDate: Date, periodStartDate: Date, periodEndDate: Date}]
}>()

const validationSchema = toTypedSchema(createManualInvoiceSchema)

const { data: assets, isFetching: isFetchingAssets, isError: assetsHasError, hasNextPage: nextAssetPageExists, isFetchingNextPage, fetchNextPage: getMoreAssets} = useWheelingAssets()

const { mutateAsync: createInvoiceAsync, isPending: isCreatingInvoice} = useCreateWheelingInvoice()

const { addSuccess, addError } = useNotifications()

const { handleSubmit } = useForm({ validationSchema })

const assetSearch = ref('')
const selectedAsset = ref<string>('')
const invoiceDate = ref<string>(todayDateOnly())
const periodStartDate = ref<string>()
const periodEndDate = ref<string>()

const assetList = computed(() => {

    const search = assetSearch.value.trim().toLowerCase()

    if(!assets.value)
        return []

    const allAssets = assets.value.pages.flatMap(page => page.items)

    const filteredAssets = !search ? allAssets : 
    allAssets.filter(asset => asset.name.toLowerCase().includes(search) 
                              || asset.assetCode.toLowerCase().includes(search)
                              || asset.projectCode.toString().includes(search))

    return filteredAssets.map((asset: any ) => ({
        assetCode: asset.assetCode,
        display: `${asset.name} (Project: ${asset.projectCode} | Asset: ${asset.assetCode}) | ${asset.type}`
    })) ?? []
})

function onCancel(){
    emit('close')
}

const onSave = handleSubmit(async values => {
    try{
        await createInvoiceAsync(values)
        addSuccess({title: 'Invoice generated successfully'})
        emit('close')
    }
    catch(ex: unknown){
        addError({title: `Unable to generate invoice for Asset Code ${selectedAsset.value}`, message: 'Please try again'})
        emit('close')

        Log.error(`Unable to generate invoice for Asset Code ${selectedAsset.value}`,{ error: ex })
    }
})

function loadAssets(){
    if(nextAssetPageExists.value && !isFetchingNextPage.value){
        getMoreAssets()
    }
}
</script>
<template>
    <ModalBase :width="610">
        <ModalTitle>WHICH ASSET ARE YOU INVOICING?</ModalTitle>
        <ModalContent>
            <ContentLoader :loading="isFetchingAssets" variant="overlay">
                <HeroSplashError v-if="assetsHasError"/>                
                <GridContainer v-if="!assetsHasError" :columns="['170px','1fr']">
                    <GridItem>
                        <InputLabel>Asset</InputLabel>
                    </GridItem>
                    <GridItem>
                        <InputSelect :items="assetList" 
                        item-value="assetCode"
                        item-text="display"
                        searchable
                        :placeholder="isFetchingAssets ? `Loading.....` : undefined"
                        v-model:selected="selectedAsset"
                        v-model:search="assetSearch"
                        :disabled="isFetchingAssets"
                        :required="true"
                        error-variant="tooltip"
                        name="assetCode"
                        :loading-more-items="isFetchingNextPage"
                        @load-more="loadAssets"/>
                    </GridItem>
                    <GridItem>
                        <InputLabel>Invoice Date</InputLabel>
                        <IconHelp class="ms-1" :tooltip="`The invoice is the invoice date, and will be reflected as such on the invoice.`" />
                    </GridItem>
                    <GridItem>
                        <InputDate v-model:value="invoiceDate" value-kind="date-only" :required="true" error-variant="tooltip" name="invoiceDate"/>
                    </GridItem>
                    <GridItem>
                        <InputLabel>Invoice Period Start Date</InputLabel>
                    </GridItem>
                    <GridItem>
                        <div :class="$style['period-range']">
                            <InputDate
                                v-model:value="periodStartDate"
                                value-kind="date-only"
                                :required="true"
                                :max="periodEndDate"
                                error-variant="tooltip"
                                name="periodStartDate"/>
                            <span :class="$style['period-separator']">-</span>
                            <InputLabel :class="$style['period-end-label']">End Date</InputLabel>
                            <InputDate
                                v-model:value="periodEndDate"
                                value-kind="date-only"
                                :required="true"
                                :min="periodStartDate"
                                error-variant="tooltip"
                                name="periodEndDate"/>
                        </div>
                    </GridItem>
                </GridContainer>
            </ContentLoader>
        </ModalContent>
        <ModalFooterCancelAndSave
         :class="$style['footer']"
         :pending="isFetchingAssets || isCreatingInvoice"
         :enabled="!isFetchingAssets && !assetsHasError"
         @cancel="onCancel"
         @save="onSave"
        />
    </ModalBase>
</template>
<style module>
.footer{
    margin-top:12px;
}

.period-range {
    display: grid;
    grid-template-columns: minmax(130px, 1fr) auto max-content minmax(130px, 1fr);
    align-items: center;
    column-gap: 12px;
}

.period-separator,
.period-end-label {
    white-space: nowrap;
}
</style>
